/**
 * TransitionBlockActionEditor — action editor for workflow_transition_block context.
 * Renders a "Block Transitions" checklist of eligible transitions.
 * Field emptiness checks are now handled in the rule conditions (is_empty operator).
 * Spec: Workflow_Transition_Prevention_Rules_Functional_Spec.md Section 7.3
 * KAN-199
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { BlockableTransitionKey } from '../../types/workflow';

interface Props {
  action: Record<string, unknown>;
  onChange: (action: Record<string, unknown>) => void;
  productTypeHint?: 'spot' | 'forward';
}

const ELIGIBLE_TRANSITIONS: { key: BlockableTransitionKey; labelKey: string; fallback: string; products: ('spot' | 'forward')[] }[] = [
  { key: 'booked_to_confirmed', labelKey: 'admin.transitionBlock.transitionBookedToConfirmed', fallback: 'Booked \u2192 Confirmed', products: ['spot', 'forward'] },
  { key: 'confirmed_to_contractSigned', labelKey: 'admin.transitionBlock.transitionConfirmedToContractSigned', fallback: 'Confirmed \u2192 Contract Signed', products: ['forward'] },
  { key: 'confirmed_to_final', labelKey: 'admin.transitionBlock.transitionConfirmedToFinal', fallback: 'Confirmed \u2192 Final', products: ['spot'] },
  { key: 'contractSigned_to_final', labelKey: 'admin.transitionBlock.transitionContractSignedToFinal', fallback: 'Contract Signed \u2192 Final', products: ['forward'] },
];

const TransitionBlockActionEditor: React.FC<Props> = ({ action, onChange, productTypeHint }) => {
  const { t } = useTranslation();

  const transitions: string[] = (action.transitions as string[]) || [];

  // Filter transitions to only those relevant to the product type
  const visibleTransitions = productTypeHint
    ? ELIGIBLE_TRANSITIONS.filter(tr => tr.products.includes(productTypeHint))
    : ELIGIBLE_TRANSITIONS;

  const handleToggleTransition = (key: BlockableTransitionKey) => {
    const updated = transitions.includes(key)
      ? transitions.filter(k => k !== key)
      : [...transitions, key];
    onChange({ ...action, transitions: updated });
  };

  return (
    <div className="transition-block-action-editor">
      <div className="action-subsection">
        <div className="action-hint">
          {t('admin.transitionBlock.transitionsHint', 'Select which transitions to block when conditions match:')}
        </div>

        <div className="transition-checklist">
          {visibleTransitions.map(({ key, labelKey, fallback }) => (
            <label key={key} className="transition-checkbox-label">
              <input
                type="checkbox"
                checked={transitions.includes(key)}
                onChange={() => handleToggleTransition(key)}
              />
              <span className="transition-label">{t(labelKey, fallback)}</span>
            </label>
          ))}
        </div>

        {transitions.length === 0 && (
          <div className="validation-error">
            {t('admin.transitionBlock.validationNoTransitions', 'At least one transition must be selected.')}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransitionBlockActionEditor;
